import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
	Box,
	Button,
	Chip,
	IconButton,
	MenuItem,
	Select,
	Stack,
	SxProps,
	Theme,
	Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ReactNode } from "react";

import { formatTimestamp, getSampleStatusLabel } from "./format";
import { changeColor, seqColor } from "./layout";
import { Disposition } from "./types";
import { RunHistoryView } from "./useRunHistory";

export const ChangedValue = ({
	previous,
	children,
}: {
	previous?: string | null;
	children: ReactNode;
}) => {
	if (previous === undefined) return <>{children}</>;

	return (
		<Tooltip arrow title={`Updated here - was ${previous ?? "empty"}`}>
			<Box
				component="span"
				sx={{ display: "inline-flex", alignItems: "center" }}
			>
				{children}
			</Box>
		</Tooltip>
	);
};

export const SoftChip = ({
	label,
	icon,
	dashed = false,
	title,
	color,
}: {
	label: ReactNode;
	icon?: ReactNode;
	dashed?: boolean;
	title?: string;
	color?: "success" | "error" | "warning" | "info" | "primary";
}) => {
	const chip = (
		<Box
			component="span"
			sx={(theme) => ({
				display: "inline-flex",
				alignItems: "center",
				gap: 0.5,
				height: 22,
				px: 1.25,
				borderRadius: 999,
				fontSize: 11,
				fontWeight: 500,
				lineHeight: 1,
				whiteSpace: "nowrap",
				color: color ? theme.palette[color].dark : theme.palette.text.secondary,
				bgcolor: color
					? alpha(theme.palette[color].main, 0.14)
					: alpha(theme.palette.text.primary, 0.06),
				border: "1px dashed",
				borderColor: dashed
					? alpha(theme.palette.text.secondary, 0.5)
					: "transparent",
				"& .MuiSvgIcon-root": { fontSize: 14 },
			})}
		>
			{icon}
			{label}
		</Box>
	);

	return title ? (
		<Tooltip title={title} arrow>
			{chip}
		</Tooltip>
	) : (
		chip
	);
};

export const SeqLabel = ({
	sequence,
	showColor,
}: {
	sequence: number;
	showColor: boolean;
}) => {
	if (!showColor) return <>{sequence}</>;

	return (
		<Box
			component="span"
			sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
		>
			<Box
				component="span"
				sx={{
					width: 8,
					height: 8,
					borderRadius: "50%",
					flexShrink: 0,
					bgcolor: (theme) => seqColor(theme, sequence),
				}}
			/>
			{sequence}
		</Box>
	);
};

export const StatusChip = ({
	code,
}: {
	code: Disposition | null | undefined;
}) => {
	if (!code)
		return <Box sx={{ width: 72, mx: "auto", textAlign: "center" }}>-</Box>;

	const status = getSampleStatusLabel(code);
	return (
		<Chip
			label={status.label}
			color={status.color}
			size="small"
			sx={{
				height: 20,
				fontSize: 11,
				width: 72,
				"& .MuiChip-label": { px: 0 },
			}}
		/>
	);
};

export const ResultChip = ({ passed }: { passed: boolean | null }) => {
	if (passed === null) return <>-</>;
	return (
		<Chip
			label={passed ? "Pass" : "Fail"}
			color={passed ? "success" : "error"}
			size="small"
			sx={{ height: 20, fontSize: 11 }}
		/>
	);
};

const DISPOSITION_OPTIONS: { value: Disposition; label: string }[] = [
	{ value: "P", label: "Passed" },
	{ value: "R", label: "ReTest" },
	{ value: "U", label: "Unknown" },
];

export const DispositionSelect = ({
	value,
	disabled,
	onChange,
	sx,
}: {
	value: Disposition;
	disabled: boolean;
	onChange: (value: Disposition) => void;
	sx?: SxProps<Theme>;
}) => (
	<Select
		value={value}
		onChange={(e) => onChange(e.target.value as Disposition)}
		disabled={disabled}
		size="small"
		sx={[
			{
				width: "80%",
				fontSize: 11,
				"& .MuiSelect-select": { py: 0.5, textAlign: "center" },
				backgroundColor: (theme) => theme.palette.background.paper,
			},
			...(Array.isArray(sx) ? sx : [sx]),
		]}
	>
		{DISPOSITION_OPTIONS.map((option) => (
			<MenuItem key={option.value} value={option.value} sx={{ fontSize: 11 }}>
				{option.label}
			</MenuItem>
		))}
	</Select>
);

const ChangeChips = ({ view }: { view: RunHistoryView }) => {
	if (view.kinds.length === 0) return null;

	return (
		<>
			<Chip
				label={view.source === "auto" ? "Auto" : "Manual"}
				size="small"
				variant="outlined"
				color={view.source === "auto" ? "default" : "primary"}
				sx={{
					height: 17,
					fontSize: 10,
					width: 54,
					"& .MuiChip-label": { px: 0 },
				}}
			/>
			{view.kinds.map((kind) => (
				<Chip
					key={kind}
					label={kind === "results" ? "Results" : "Disposition"}
					size="small"
					color={changeColor(kind, view.group.disposition?.Disposition)}
					sx={{
						height: 17,
						fontSize: 10,
						width: 74,
						"& .MuiChip-label": { px: 0 },
					}}
				/>
			))}
		</>
	);
};

export const RunHistoryBar = ({ view }: { view: RunHistoryView }) => (
	<Stack
		direction="row"
		spacing={1}
		alignItems="center"
		flexWrap="wrap"
		useFlexGap
		sx={{ py: 0.25, fontSize: 11 }}
	>
		<Tooltip title="Earlier" arrow>
			<span>
				<IconButton
					size="small"
					disabled={view.position <= 1}
					onClick={view.back}
				>
					<ChevronLeftIcon fontSize="small" />
				</IconButton>
			</span>
		</Tooltip>
		<Tooltip title="Later" arrow>
			<span>
				<IconButton
					size="small"
					disabled={view.isCurrent || !view.at}
					onClick={view.forward}
				>
					<ChevronRightIcon fontSize="small" />
				</IconButton>
			</span>
		</Tooltip>

		{view.at ? (
			<>
				<Box sx={{ fontWeight: 600 }}>{formatTimestamp(view.at)}</Box>
				<ChangeChips view={view} />
			</>
		) : (
			<Box sx={{ fontWeight: 600 }}>
				{view.isLoading ? "Loading history…" : "Nothing recorded yet"}
			</Box>
		)}

		{view.total > 0 && (
			<Box sx={{ color: "text.secondary" }}>
				({view.position} of {view.total})
			</Box>
		)}

		<Box sx={{ flex: 1 }} />

		<Button size="small" sx={{ fontSize: 11 }} onClick={view.close}>
			Back to current
		</Button>
	</Stack>
);
